import { HistoryEntry, formatHistoryTimestamp } from '../hooks/useHistory';
import { CalculatorState } from '../hooks/useOffsetCalculator';
import { PRESETTER_MODES } from '../utils/constants';
import { formatNumber } from '../utils/binaryConversion';

interface HistoryPanelProps {
  history: HistoryEntry[];
  onLoadEntry: (state: CalculatorState) => void;
  onRemoveEntry: (id: string) => void;
  onClearHistory: () => void;
}

export function HistoryPanel({
  history,
  onLoadEntry,
  onRemoveEntry,
  onClearHistory
}: HistoryPanelProps) {
  if (history.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
          Calculation History
        </h2>
        <div className="p-6 text-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-lg">
          No saved calculations yet
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
          Calculation History
        </h2>
        <button
          onClick={onClearHistory}
          className="px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors"
        >
          Clear All
        </button>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {history.map((entry) => (
          <div
            key={entry.id}
            className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <span className="inline-block px-2 py-0.5 text-xs font-medium bg-industrial-100 dark:bg-industrial-900 text-industrial-700 dark:text-industrial-300 rounded">
                  {PRESETTER_MODES[entry.state.mode].label}
                </span>
                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                  {formatHistoryTimestamp(entry.timestamp)}
                </span>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => onLoadEntry(entry.state)}
                  className="px-2 py-1 text-xs bg-industrial-500 hover:bg-industrial-600 text-white rounded transition-colors"
                >
                  Load
                </button>
                <button
                  onClick={() => onRemoveEntry(entry.id)}
                  className="px-2 py-1 text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                  aria-label="Remove entry"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-gray-600 dark:text-gray-400">
                Original: <span className="font-mono">{formatNumber(entry.result.originalOffset)}</span>
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                New: <span className="font-mono font-medium text-green-600 dark:text-green-400">{formatNumber(entry.result.newOffset)}</span>
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                CNC: <span className="font-mono">{entry.state.cncDiameter}</span> mm
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                Measured: <span className="font-mono">{entry.state.measuredDiameter}</span> mm
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
